/**
 * Commodity Trade Booking Page (KAN-259, Step 13)
 *
 * Two-panel layout (like FX Trade Booking):
 *   Top panel:  Import workflow — select config → upload file → preview → commit
 *   Bottom panel: CommodityTradesGrid showing committed trades
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, CellValueChangedEvent } from 'ag-grid-community';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import ExcelJS from 'exceljs';
import { useAuth } from '../../../components/auth/AuthContext';
import { tradeImportService, TradeImportConfig, TradeImportResult, StagedImport, StagedImportRow } from '../../../services/api/tradeImportService';
import { clientService } from '../../../services/clientService';
import { clientService as apiClientService } from '../../../services/api/clientService';
import { masterDataService } from '../../../services/api/masterDataService';
import { getEnumsForModule } from '../../../constants/tradeFields';
import CommodityTradesGrid from '../../../components/grids/CommodityTradesGrid';
import SystemDateDisplay from '../../../components/common/SystemDateDisplay';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import ResizeHandle from '../../../components/common/ResizeHandle';
import Toast from '../../../components/common/Toast';
import AlertModal from '../../../components/common/AlertModal';
import { useSystemDate } from '../../../hooks/useSystemDate';
import { translateImportError } from '../../../utils/importErrors';
import './CommodityTradeBooking.css';

type ImportStep = 'idle' | 'selecting' | 'uploading' | 'previewing' | 'staging' | 'enriching' | 'committing';
type PreviewFilter = 'all' | 'valid' | 'errors' | 'warnings';

const MODULE = 'Commodities';

// Date fields that should be formatted as DD/MM/YYYY in the preview table
const DATE_FIELDS = new Set(['TradeDate', 'ValueDate', 'MaturityDate', 'PaymentDate']);

// Numeric fields with their decimal precision for display formatting
const NUMERIC_FIELD_DECIMALS: Record<string, number> = {
  Price: 4,
  Quantity: 2,
  Lots: 0,
  Premium: 2,
  SettlementAmount: 2,
};

// Fixed column order for the preview table (# / Status / Issues are handled separately)
const PREVIEW_COLUMN_ORDER = [
  'OurParty',
  'ProductType',
  'UnderlyingName',
  'Direction',
  'CounterpartyName',
  'InternalCounterparty',
  'TradeDate',
  'MaturityDate',
  'Lots',
  'Price',
  'UnitOfMeasure',
  'Quantity',
  'VenueName',
  'FixingReference',
];

/** Convert PascalCase field key to camelCase for i18n lookup */
function fieldToCamelCase(field: string): string {
  return field.charAt(0).toLowerCase() + field.slice(1);
}

/** Format YYYY-MM-DD → DD/MM/YYYY for display */
function formatDateForDisplay(value: any): string {
  if (typeof value !== 'string' || !value) return value ?? '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

const CommodityTradeBooking: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id || '';
  const commodityRole = user?.moduleRoles?.Commodities;
  const canImport = commodityRole === 'client_admin'
    || commodityRole === 'client_fo_user'
    || commodityRole === 'client_fo_bo_user';
  const systemDateResult = useSystemDate(clientId);

  // ── Bottom grid state ──
  const [trades, setTrades] = useState<any[]>([]);
  const [tradesLoading, setTradesLoading] = useState(true);

  // ── Date range filter — defaults to current calendar month ──
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const monthEndStr = `${monthEnd.getFullYear()}-${String(monthEnd.getMonth() + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(monthEndStr);

  // ── Quick month picker options (18 months back, 6 forward) ──
  const monthOptions = useMemo(() => {
    const opts: { value: string; label: string; from: string; to: string }[] = [];
    const locale = i18n.language || 'en';
    const fmt = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' });
    for (let offset = -18; offset <= 6; offset++) {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const mStr = String(m).padStart(2, '0');
      const lastDay = new Date(y, m, 0).getDate();
      opts.push({
        value: `${y}-${mStr}`,
        label: fmt.format(d),
        from: `${y}-${mStr}-01`,
        to: `${y}-${mStr}-${String(lastDay).padStart(2, '0')}`,
      });
    }
    return opts;
  }, [i18n.language]);

  // Derive current quick-month value from dateFrom (matches if dateFrom is 1st of a month)
  const quickMonthValue = dateFrom?.match(/^(\d{4}-\d{2})-01$/) ? dateFrom.slice(0, 7) : '';

  const handleQuickMonth = (val: string) => {
    const opt = monthOptions.find((o) => o.value === val);
    if (opt) {
      setDateFrom(opt.from);
      setDateTo(opt.to);
    }
  };

  // ── Import workflow state ──
  const [importStep, setImportStep] = useState<ImportStep>('idle');
  const [configs, setConfigs] = useState<TradeImportConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [fileBase64, setFileBase64] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileRowCount, setFileRowCount] = useState<number | null>(null);
  const [previewResult, setPreviewResult] = useState<TradeImportResult | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Staged import state ──
  const [stagedImports, setStagedImports] = useState<StagedImport[]>([]);
  const [currentStagedImport, setCurrentStagedImport] = useState<StagedImport | null>(null);

  // ── Label modal (shown before staging to name the staged import) ──
  const [labelModal, setLabelModal] = useState<{ isOpen: boolean }>({ isOpen: false });
  const [pendingLabel, setPendingLabel] = useState('');

  // ── Inline rename state ──
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // ── Enrichment grid state ──
  const [enrichmentRows, setEnrichmentRows] = useState<StagedImportRow[]>([]);
  const [enrichmentDirty, setEnrichmentDirty] = useState(false);
  const [fieldEnums, setFieldEnums] = useState<Record<string, { value: string; label: string }[]>>({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [committingStaged, setCommittingStaged] = useState(false);
  const enrichmentGridRef = useRef<AgGridReact>(null);

  // ── Confirm modal ──
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // ── Toast ──
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning'; visible: boolean }>({
    message: '',
    type: 'success',
    visible: false,
  });

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ message, type, visible: true });
  }, []);

  // ── Load trades for bottom grid ──
  const loadTrades = useCallback(async () => {
    if (!clientId) return;
    try {
      setTradesLoading(true);
      const data = await clientService.getCommodityTrades(clientId, dateFrom || undefined, dateTo || undefined);
      setTrades(data);
    } catch (err) {
      console.error('Error loading commodity trades:', err);
    } finally {
      setTradesLoading(false);
    }
  }, [clientId, dateFrom, dateTo]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  // ── Load import configs when user clicks Import ──
  const handleStartImport = useCallback(async () => {
    setImportStep('selecting');
    try {
      const cfgs = await tradeImportService.listConfigs(clientId, MODULE);
      const enabledConfigs = cfgs.filter((c) => c.enabled && c.importContext !== 'Expiry');
      setConfigs(enabledConfigs);
      if (enabledConfigs.length === 1) {
        setSelectedConfigId(enabledConfigs[0].id || '');
      }
    } catch (err) {
      console.error('Error loading import configs:', err);
      showToast(t('commodities.tradeBooking.loadConfigsError'), 'error');
      setImportStep('idle');
    }
  }, [clientId, t, showToast]);

  // ── Cancel / reset import workflow ──
  const handleCancelImport = useCallback(() => {
    setImportStep('idle');
    setSelectedConfigId('');
    setPreviewFilter('all');
    setFileBase64('');
    setFileName('');
    setFileRowCount(null);
    setPreviewResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ── File selection ──
  const selectedConfig = useMemo(() => {
    return configs.find((c) => c.id === selectedConfigId) || null;
  }, [configs, selectedConfigId]);

  const fileAccept = useMemo(() => {
    if (!selectedConfig) return '.csv,.xlsx,.xls';
    if (selectedConfig.sourceType === 'CSV') return '.csv';
    if (selectedConfig.sourceType === 'EXCEL') return '.xlsx,.xls';
    return '.csv,.xlsx,.xls';
  }, [selectedConfig]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setFileRowCount(null);

    // Read as base64 for the API
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setFileBase64(base64);
    };
    reader.readAsDataURL(file);

    // Quick row count for the overlay message (skip empty rows)
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (ext === 'csv') {
      file.text().then((text) => {
        const lines = text.split('\n').filter((l) => {
          // Only count lines that have at least one non-empty, non-delimiter value
          const stripped = l.replace(/[,;\t]/g, '').trim();
          return stripped.length > 0;
        });
        setFileRowCount(Math.max(0, lines.length - 1)); // subtract header
      }).catch(() => {});
    } else if (ext === 'xlsx' || ext === 'xls') {
      file.arrayBuffer().then(async (buf) => {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buf);
        const ws = wb.worksheets[0];
        if (ws) {
          let dataRows = 0;
          ws.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return; // skip header
            const hasData = row.values
              ? (Array.isArray(row.values) ? row.values : Object.values(row.values))
                  .some((v) => v !== null && v !== undefined && String(v).trim() !== '')
              : false;
            if (hasData) dataRows++;
          });
          setFileRowCount(dataRows);
        }
      }).catch(() => {});
    }
  }, []);

  // ── Preview ──
  const handlePreview = useCallback(async () => {
    if (!selectedConfigId || !fileBase64 || !fileName) return;

    setImportLoading(true);
    setImportStep('uploading');
    try {
      const result = await tradeImportService.importTrades(clientId, MODULE, {
        configId: selectedConfigId,
        fileBase64,
        fileName,
        mode: 'preview',
      });
      setPreviewResult(result);
      setImportStep('previewing');
    } catch (err: any) {
      console.error('Preview failed:', err);
      showToast(err.message || t('commodities.tradeBooking.previewError'), 'error');
      setImportStep('selecting');
    } finally {
      setImportLoading(false);
    }
  }, [clientId, selectedConfigId, fileBase64, fileName, t, showToast]);

  // ── Commit ──
  const handleCommit = useCallback(async () => {
    if (!selectedConfigId || !fileBase64 || !fileName) return;

    setImportLoading(true);
    setImportStep('committing');
    try {
      const result = await tradeImportService.importTrades(clientId, MODULE, {
        configId: selectedConfigId,
        fileBase64,
        fileName,
        mode: 'commit',
      });

      if (result.tradesCreated > 0) {
        showToast(
          t('commodities.tradeBooking.commitSuccess', { count: result.tradesCreated }),
          'success'
        );
        // Refresh the bottom grid
        loadTrades();
      } else {
        showToast(t('commodities.tradeBooking.commitNoTrades'), 'warning');
      }

      handleCancelImport();
    } catch (err: any) {
      console.error('Commit failed:', err);
      showToast(err.message || t('commodities.tradeBooking.commitError'), 'error');
      setImportStep('previewing');
    } finally {
      setImportLoading(false);
    }
  }, [clientId, selectedConfigId, fileBase64, fileName, t, showToast, loadTrades, handleCancelImport]);

  // ── Load staged imports list ──
  const loadStagedImports = useCallback(async () => {
    if (!clientId) return;
    try {
      const list = await tradeImportService.listStagedImports(clientId, MODULE);
      setStagedImports(list);
    } catch (err) {
      console.error('Error loading staged imports:', err);
    }
  }, [clientId]);

  useEffect(() => {
    loadStagedImports();
  }, [loadStagedImports]);

  // ── AG Grid locale ──
  const localeText = useMemo(() => {
    if (i18n.language === 'es') return AG_GRID_LOCALE_ES;
    if (i18n.language === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  // ── Initialize enrichment rows + load enums when a NEW staged import is opened ──
  // Depends on the staged import ID, not the full object, so that updating
  // metadata (enrichedRows, updatedAt) after Save Draft does NOT re-trigger
  // this effect and overwrite the user's edits.
  const currentStagedId = currentStagedImport?.id;
  useEffect(() => {
    if (importStep !== 'enriching' || !currentStagedImport || !currentStagedId) return;

    // Populate enrichmentRows from staged import
    setEnrichmentRows(currentStagedImport.rows || []);
    setEnrichmentDirty(false);

    // Load enum values for deferred fields
    const loadEnums = async () => {
      const enums: Record<string, { value: string; label: string }[]> = {};

      // Static enums from the field registry
      const moduleEnums = getEnumsForModule(MODULE);
      for (const [field, values] of Object.entries(moduleEnums)) {
        enums[field] = values.map((v) => ({ value: v.value, label: t(v.labelKey) }));
      }

      // Dynamic enums from master data / client data
      try {
        const cpResponse = await apiClientService.getCounterpartyNames(clientId, 'commodity');
        if (cpResponse.data?.length) {
          enums.CounterpartyName = cpResponse.data.map((cp: any) => ({
            value: cp.counterpartyName || cp,
            label: cp.counterpartyName || cp,
          }));
        }
      } catch { /* optional */ }

      try {
        const entitiesResponse = await apiClientService.getTradingEntities(clientId);
        if (entitiesResponse.data?.length) {
          enums.OurParty = entitiesResponse.data.map((e: any) => ({ value: e.name || e.id, label: e.name || e.id }));
        }
      } catch { /* optional */ }

      try {
        const underlyings = await masterDataService.listUnderlyings('Commodity');
        if (underlyings.length) {
          enums.UnderlyingCode = underlyings.map((u) => ({ value: u.code, label: `${u.code} — ${u.name}` }));
        }
      } catch { /* optional */ }

      try {
        const venues = await masterDataService.listVenues();
        if (venues.length) {
          enums.VenueCode = venues.map((v) => ({ value: v.code, label: `${v.code} — ${v.name}` }));
        }
      } catch { /* optional */ }

      try {
        const contracts = await masterDataService.listExchangeContracts();
        if (contracts.length) {
          enums.ExchangeContractCode = contracts.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }));
        }
      } catch { /* optional */ }

      setFieldEnums(enums);
    };

    loadEnums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importStep, currentStagedId, clientId, t]);

  // ── Translate column header: try commodities.positions.grid.<camelCase>, fall back to key ──
  const getColumnLabel = useCallback((fieldKey: string): string => {
    const i18nKey = `commodities.positions.grid.${fieldToCamelCase(fieldKey)}`;
    const translated = t(i18nKey);
    return translated === i18nKey ? fieldKey : translated;
  }, [t]);

  // ── Enrichment AG Grid column definitions ──
  const enrichmentColDefs = useMemo<ColDef[]>(() => {
    if (!currentStagedImport) return [];

    const deferredSet = new Set(currentStagedImport.deferredFields || []);

    // Row index column
    const cols: ColDef[] = [
      { field: 'rowIndex', headerName: '#', width: 60, pinned: 'left', editable: false },
    ];

    // Build columns from PREVIEW_COLUMN_ORDER first, then any deferred fields not already in that list
    const shownFields = new Set<string>();
    for (const field of PREVIEW_COLUMN_ORDER) {
      shownFields.add(field);
      const isDeferred = deferredSet.has(field);
      const enumValues = fieldEnums[field];

      const col: ColDef = {
        field: `fields.${field}`,
        headerName: getColumnLabel(field),
        width: isDeferred ? 160 : 130,
        editable: isDeferred,
        cellClassRules: isDeferred ? {
          'enrichment-cell-deferred': () => true,
          'enrichment-cell-empty': (params: any) => !params.value || params.value === '',
        } : undefined,
      };

      // Date fields: format for display
      if (DATE_FIELDS.has(field) && !isDeferred) {
        col.valueFormatter = (params: any) => formatDateForDisplay(params.value);
      }

      // Numeric fields: format for display
      const decimals = NUMERIC_FIELD_DECIMALS[field];
      if (decimals !== undefined && !isDeferred) {
        col.type = 'numericColumn';
        col.valueFormatter = (params: any) => {
          if (params.value == null || params.value === '') return '';
          const num = typeof params.value === 'number' ? params.value : parseFloat(String(params.value));
          if (isNaN(num)) return String(params.value);
          return num.toLocaleString(i18n.language, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        };
      }

      // Deferred fields with enum values: use select cell editor
      if (isDeferred && enumValues?.length) {
        col.cellEditor = 'agSelectCellEditor';
        col.cellEditorParams = {
          values: ['', ...enumValues.map((e) => e.value)],
        };
      }

      // Date deferred fields: text input (user types YYYY-MM-DD or DD/MM/YYYY)
      if (isDeferred && DATE_FIELDS.has(field)) {
        col.valueFormatter = (params: any) => formatDateForDisplay(params.value);
      }

      cols.push(col);
    }

    // Add any deferred fields not already in PREVIEW_COLUMN_ORDER
    for (const field of currentStagedImport.deferredFields || []) {
      if (shownFields.has(field)) continue;
      shownFields.add(field);
      const enumValues = fieldEnums[field];

      const col: ColDef = {
        field: `fields.${field}`,
        headerName: getColumnLabel(field),
        width: 160,
        editable: true,
        cellClassRules: {
          'enrichment-cell-deferred': () => true,
          'enrichment-cell-empty': (params: any) => !params.value || params.value === '',
        },
      };

      if (enumValues?.length) {
        col.cellEditor = 'agSelectCellEditor';
        col.cellEditorParams = {
          values: ['', ...enumValues.map((e) => e.value)],
        };
      }

      if (DATE_FIELDS.has(field)) {
        col.valueFormatter = (params: any) => formatDateForDisplay(params.value);
      }

      cols.push(col);
    }

    return cols;
  }, [currentStagedImport, fieldEnums, getColumnLabel, i18n.language]);

  // ── Rows that need enrichment (have at least one DEFERRED_FIELD warning) ──
  const rowsNeedingEnrichment = useMemo(() => {
    return new Set(
      enrichmentRows
        .filter((row) =>
          row.warnings.some((w) => w.includes('DEFERRED_FIELD') || w.includes('is deferred'))
        )
        .map((row) => row.rowIndex)
    );
  }, [enrichmentRows]);

  // ── Enrichment row data (flatten fields for AG Grid) ──
  // Only show rows that have deferred field warnings
  const enrichmentRowData = useMemo(() => {
    return enrichmentRows
      .filter((row) => rowsNeedingEnrichment.has(row.rowIndex))
      .map((row) => ({
        rowIndex: row.rowIndex,
        fields: { ...row.fields },
      }));
  }, [enrichmentRows, rowsNeedingEnrichment]);

  // ── Enriched row count (live calculation) ──
  const enrichedCount = useMemo(() => {
    if (!currentStagedImport) return 0;
    const deferredFields = currentStagedImport.deferredFields || [];
    if (deferredFields.length === 0) return enrichmentRows.length;
    // Only count among rows that actually need enrichment
    return enrichmentRows
      .filter((row) => rowsNeedingEnrichment.has(row.rowIndex))
      .filter((row) =>
        deferredFields.every((df) => row.fields[df] != null && row.fields[df] !== '')
      ).length;
  }, [enrichmentRows, currentStagedImport, rowsNeedingEnrichment]);

  // ── Handle cell value change in AG Grid ──
  const handleEnrichmentCellChange = useCallback((event: CellValueChangedEvent) => {
    const rowIndex = event.data.rowIndex;
    const colField = event.colDef.field; // e.g. "fields.CounterpartyName"
    if (!colField?.startsWith('fields.')) return;

    const fieldName = colField.replace('fields.', '');
    const newValue = event.newValue;

    setEnrichmentRows((prev) =>
      prev.map((row) =>
        row.rowIndex === rowIndex
          ? { ...row, fields: { ...row.fields, [fieldName]: newValue } }
          : row
      )
    );
    setEnrichmentDirty(true);
  }, []);

  // ── Paste handler for AG Grid (Community edition doesn't support paste natively) ──
  // Uses onPasteCapture to intercept before the cell editor's input can consume it.
  const handleEnrichmentPaste = useCallback((event: React.ClipboardEvent) => {
    const gridApi = enrichmentGridRef.current?.api;
    if (!gridApi) return;

    // Stop any active cell editing first so it doesn't consume the paste
    gridApi.stopEditing(true);

    const focusedCell = gridApi.getFocusedCell();
    if (!focusedCell) return;

    // Only allow paste into editable (deferred) columns
    const colDef = focusedCell.column.getColDef();
    if (!colDef.editable) return;

    event.preventDefault();
    event.stopPropagation();

    const clipboardText = event.clipboardData.getData('text/plain');
    if (!clipboardText) return;

    // Parse clipboard: rows split by newline, cells split by tab
    const clipRows = clipboardText
      .split(/\r?\n/)
      .filter((line) => line.length > 0)
      .map((line) => line.split('\t'));

    // Get all visible columns that are editable
    const allColumns = gridApi.getAllDisplayedColumns();
    const startColIndex = allColumns.findIndex((c) => c.getColId() === focusedCell.column.getColId());
    if (startColIndex < 0) return;

    const startRowIndex = focusedCell.rowIndex;
    const updates: { rowIndex: number; fieldName: string; value: string }[] = [];

    for (let r = 0; r < clipRows.length; r++) {
      const gridRowIndex = startRowIndex + r;
      const rowNode = gridApi.getDisplayedRowAtIndex(gridRowIndex);
      if (!rowNode) break;

      for (let c = 0; c < clipRows[r].length; c++) {
        const colIndex = startColIndex + c;
        if (colIndex >= allColumns.length) break;

        const col = allColumns[colIndex];
        const targetColDef = col.getColDef();
        if (!targetColDef.editable) continue;

        const colField = targetColDef.field;
        if (!colField?.startsWith('fields.')) continue;

        const fieldName = colField.replace('fields.', '');
        const value = clipRows[r][c].trim();

        // Validate against allowed enum values if the field has a dropdown
        const allowedValues = fieldEnums[fieldName];
        if (allowedValues?.length && value !== '') {
          const isValid = allowedValues.some((e) => e.value === value);
          if (!isValid) continue; // skip invalid values
        }

        // Update the grid display
        rowNode.setDataValue(colField, value);

        updates.push({ rowIndex: rowNode.data.rowIndex, fieldName, value });
      }
    }

    if (updates.length > 0) {
      setEnrichmentRows((prev) => {
        const newRows = [...prev];
        for (const upd of updates) {
          const idx = newRows.findIndex((row) => row.rowIndex === upd.rowIndex);
          if (idx >= 0) {
            newRows[idx] = {
              ...newRows[idx],
              fields: { ...newRows[idx].fields, [upd.fieldName]: upd.value },
            };
          }
        }
        return newRows;
      });
      setEnrichmentDirty(true);
    }
  }, [fieldEnums]);

  // ── Save Draft ──
  const handleSaveDraft = useCallback(async () => {
    if (!currentStagedImport?.id) return;

    setSavingDraft(true);
    try {
      const result = await tradeImportService.saveStagedImportDraft(
        clientId,
        MODULE,
        currentStagedImport.id,
        enrichmentRows
      );

      setCurrentStagedImport((prev) =>
        prev ? { ...prev, enrichedRows: result.enrichedRows, updatedAt: result.updatedAt } : prev
      );
      setEnrichmentDirty(false);
      showToast(t('commodities.tradeBooking.draftSaved'), 'success');
      loadStagedImports();
    } catch (err: any) {
      console.error('Error saving draft:', err);
      showToast(err.message || t('commodities.tradeBooking.draftSaveError'), 'error');
    } finally {
      setSavingDraft(false);
    }
  }, [currentStagedImport, clientId, enrichmentRows, t, showToast, loadStagedImports]);

  // ── Commit staged import ──
  const handleCommitStaged = useCallback(async () => {
    if (!currentStagedImport?.id) return;

    // Auto-save before committing if dirty
    if (enrichmentDirty) {
      setSavingDraft(true);
      try {
        await tradeImportService.saveStagedImportDraft(
          clientId,
          MODULE,
          currentStagedImport.id,
          enrichmentRows
        );
        setEnrichmentDirty(false);
      } catch (err: any) {
        console.error('Error saving draft before commit:', err);
        showToast(err.message || t('commodities.tradeBooking.draftSaveError'), 'error');
        setSavingDraft(false);
        return;
      } finally {
        setSavingDraft(false);
      }
    }

    setCommittingStaged(true);
    setImportStep('committing');
    try {
      const result = await tradeImportService.commitStagedImport(
        clientId,
        MODULE,
        currentStagedImport.id
      );

      showToast(
        t('commodities.tradeBooking.commitSuccess', { count: result.tradesCreated }),
        'success'
      );

      // Reset enrichment state
      setCurrentStagedImport(null);
      setEnrichmentRows([]);
      setEnrichmentDirty(false);
      handleCancelImport();
      loadTrades();
      loadStagedImports();
    } catch (err: any) {
      console.error('Commit failed:', err);
      // Check for 422 validation errors
      const detail = err.response?.data?.detail || err.message;
      if (typeof detail === 'object' && detail.rowErrors) {
        showToast(
          t('commodities.tradeBooking.commitValidationError', { count: detail.rowErrors.length }),
          'error'
        );
      } else {
        showToast(typeof detail === 'string' ? detail : t('commodities.tradeBooking.commitError'), 'error');
      }
      setImportStep('enriching');
    } finally {
      setCommittingStaged(false);
    }
  }, [currentStagedImport, enrichmentDirty, enrichmentRows, clientId, t, showToast, handleCancelImport, loadTrades, loadStagedImports]);

  // ── Close enrichment with unsaved changes guard ──
  const handleCloseEnrichment = useCallback(() => {
    if (enrichmentDirty) {
      setConfirmModal({
        isOpen: true,
        title: t('commodities.tradeBooking.unsavedChanges'),
        message: t('commodities.tradeBooking.unsavedChangesMessage'),
        confirmLabel: t('commodities.tradeBooking.discardChanges'),
        onConfirm: () => {
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          setCurrentStagedImport(null);
          setEnrichmentRows([]);
          setEnrichmentDirty(false);
          handleCancelImport();
          loadStagedImports();
        },
      });
    } else {
      setCurrentStagedImport(null);
      setEnrichmentRows([]);
      handleCancelImport();
      loadStagedImports();
    }
  }, [enrichmentDirty, t, handleCancelImport, loadStagedImports]);

  // ── beforeunload guard for unsaved enrichment changes ──
  useEffect(() => {
    if (!enrichmentDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enrichmentDirty]);

  // ── Detect whether the preview has deferred field warnings ──
  const hasDeferredWarnings = useMemo(() => {
    if (!previewResult?.trades) return false;
    return previewResult.trades.some((row) =>
      row.warnings.some((w) => w.includes('DEFERRED_FIELD') || w.includes('is deferred'))
    );
  }, [previewResult]);

  // ── Effective deferred fields for this import (from config) ──
  const effectiveDeferredFields = useMemo(() => {
    return selectedConfig?.deferredFields || [];
  }, [selectedConfig]);

  // ── Stage for Enrichment — step 1: show label modal ──
  const handleStageImport = useCallback(() => {
    if (!previewResult || !selectedConfig) return;
    // Generate default label: "{configName} — DD/MM/YYYY HH:MM" (local time)
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const defaultLabel = (
      `${selectedConfig.name} — ` +
      `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ` +
      `${pad(now.getHours())}:${pad(now.getMinutes())}`
    );
    setPendingLabel(defaultLabel);
    setLabelModal({ isOpen: true });
  }, [previewResult, selectedConfig]);

  // ── Stage for Enrichment — step 2: create the staged import with label ──
  const handleLabelConfirm = useCallback(async () => {
    if (!previewResult || !selectedConfig) return;
    setLabelModal({ isOpen: false });

    setImportLoading(true);
    setImportStep('staging');
    try {
      const stagingRows: StagedImportRow[] = previewResult.trades
        .filter((row) => row.errors.length === 0)
        .map((row) => ({
          rowIndex: row.rowIndex,
          fields: row.fields,
          warnings: row.warnings,
        }));

      const result = await tradeImportService.createStagedImport(clientId, MODULE, {
        label: pendingLabel.trim() || undefined,
        importContext: 'Subscription',
        configId: selectedConfig.id || '',
        configName: selectedConfig.name,
        fileName,
        deferredFields: effectiveDeferredFields,
        rows: stagingRows,
        skippedRows: previewResult.skippedRows,
      });

      setCurrentStagedImport({ ...result, rows: stagingRows });
      setImportStep('enriching');
      loadStagedImports();
      showToast(t('commodities.tradeBooking.stagingSuccess'), 'success');
    } catch (err: any) {
      console.error('Staging failed:', err);
      showToast(err.message || t('commodities.tradeBooking.stagingError'), 'error');
      setImportStep('previewing');
    } finally {
      setImportLoading(false);
    }
  }, [previewResult, selectedConfig, clientId, fileName, effectiveDeferredFields, pendingLabel, t, showToast, loadStagedImports]);

  // ── Inline rename — submit ──
  const handleRenameSubmit = useCallback(async (id: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    setRenameSaving(true);
    try {
      const result = await tradeImportService.renameStagedImport(clientId, MODULE, id, trimmed);
      setStagedImports((prev) =>
        prev.map((s) => s.id === id ? { ...s, label: result.label } : s)
      );
      if (currentStagedImport?.id === id) {
        setCurrentStagedImport((prev) => prev ? { ...prev, label: result.label } : prev);
      }
      setRenamingId(null);
    } catch (err: any) {
      console.error('Rename failed:', err);
      showToast(t('commodities.tradeBooking.renameError'), 'error');
    } finally {
      setRenameSaving(false);
    }
  }, [clientId, renameValue, currentStagedImport, t, showToast]);

  // ── Resume a staged import from the list ──
  const handleResumeStagedImport = useCallback(async (stagedId: string) => {
    setImportLoading(true);
    try {
      const staged = await tradeImportService.getStagedImport(clientId, MODULE, stagedId);
      if (!staged) {
        showToast(t('commodities.tradeBooking.stagedNotFound'), 'error');
        return;
      }
      setCurrentStagedImport(staged);
      setImportStep('enriching');
    } catch (err: any) {
      console.error('Error loading staged import:', err);
      showToast(err.message || t('commodities.tradeBooking.loadStagedError'), 'error');
    } finally {
      setImportLoading(false);
    }
  }, [clientId, t, showToast]);

  // ── Delete a staged import ──
  const handleDeleteStagedImport = useCallback(async (stagedId: string) => {
    try {
      await tradeImportService.deleteStagedImport(clientId, MODULE, stagedId);
      showToast(t('commodities.tradeBooking.stagedDeleted'), 'success');
      loadStagedImports();
      // If we were viewing this one, go back to idle
      if (currentStagedImport?.id === stagedId) {
        setCurrentStagedImport(null);
        setImportStep('idle');
      }
    } catch (err: any) {
      console.error('Error deleting staged import:', err);
      showToast(err.message || t('commodities.tradeBooking.deleteStagedError'), 'error');
    }
  }, [clientId, t, showToast, loadStagedImports, currentStagedImport]);

  // ── Derive preview columns from first trade row ──
  const previewColumns = useMemo(() => {
    if (!previewResult?.trades?.length) return [];
    return PREVIEW_COLUMN_ORDER;
  }, [previewResult]);

  // ── Format cell value (dates → DD/MM/YYYY, numbers → locale-formatted) ──
  const formatCellValue = useCallback((fieldKey: string, value: any): string => {
    if (DATE_FIELDS.has(fieldKey)) return formatDateForDisplay(value);
    const dec = NUMERIC_FIELD_DECIMALS[fieldKey];
    if (dec !== undefined && value != null && value !== '') {
      const num = typeof value === 'number' ? value : parseFloat(String(value));
      if (!isNaN(num)) return num.toLocaleString(i18n.language, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    }
    return value ?? '';
  }, [i18n.language]);

  // ── Filter preview trades based on selected summary stat ──
  const filteredPreviewTrades = useMemo(() => {
    if (!previewResult?.trades) return [];
    if (previewFilter === 'all') return previewResult.trades;
    return previewResult.trades.filter((row) => {
      if (previewFilter === 'errors') return row.errors.length > 0;
      if (previewFilter === 'warnings') return row.warnings.length > 0 && row.errors.length === 0;
      if (previewFilter === 'valid') return row.errors.length === 0 && row.warnings.length === 0;
      return true;
    });
  }, [previewResult, previewFilter]);

  // ──────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────

  return (
    <div className="commodity-booking-page">
      {/* Page header — matches FX Trade Booking */}
      <div className="commodity-booking-header">
        <div className="commodity-booking-title">{t('commodities.tradeBooking.title')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <select
            className="commodity-quick-month"
            value={quickMonthValue}
            onChange={(e) => handleQuickMonth(e.target.value)}
            disabled={tradesLoading}
          >
            <option value="" disabled>{t('common.month', 'Month')}</option>
            {monthOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onApply={(from, to) => { setDateFrom(from); setDateTo(to); }}
            disabled={tradesLoading}
          />
          <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
        </div>
      </div>

      <PanelGroup orientation="vertical" className="commodity-booking-panel-group">
        {/* ── Top Panel: Import Section ── */}
        <Panel defaultSize={50} minSize={20}>
          <div className="commodity-import-section">
            {/* Idle state — staged imports list + import button */}
            {importStep === 'idle' && (
              <>
                {stagedImports.filter((s) => s.status === 'pending').length > 0 ? (
                  <div className="commodity-staged-list">
                    <div className="commodity-staged-list-header">
                      <span className="commodity-staged-list-title">
                        {t('commodities.tradeBooking.stagedImports')}
                      </span>
                      {canImport && (
                        <button className="commodity-import-trigger-btn" onClick={handleStartImport}>
                          {t('commodities.tradeBooking.importButton')}
                        </button>
                      )}
                    </div>
                    {stagedImports
                      .filter((s) => s.status === 'pending')
                      .map((staged) => (
                        <div key={staged.id} className="commodity-staged-item">
                          <div className="commodity-staged-item-info">
                            {/* Label row — inline rename when pencil clicked */}
                            <div className="commodity-staged-item-label-row">
                              {renamingId === staged.id ? (
                                <input
                                  ref={renameInputRef}
                                  className="commodity-staged-rename-input"
                                  value={renameValue}
                                  disabled={renameSaving}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      staged.id && handleRenameSubmit(staged.id);
                                    } else if (e.key === 'Escape') {
                                      setRenamingId(null);
                                    }
                                  }}
                                  onBlur={() => {
                                    if (!renameSaving) setRenamingId(null);
                                  }}
                                  placeholder={t('commodities.tradeBooking.rename.placeholder')}
                                  title={t('commodities.tradeBooking.rename.saveHint')}
                                />
                              ) : (
                                <>
                                  <span className="commodity-staged-item-name">
                                    {staged.label || staged.configName}
                                  </span>
                                  <button
                                    className="commodity-staged-rename-btn"
                                    title={t('commodities.tradeBooking.rename.placeholder')}
                                    onClick={() => {
                                      setRenameValue(staged.label || staged.configName);
                                      setRenamingId(staged.id || null);
                                      // Focus the input on next render
                                      setTimeout(() => renameInputRef.current?.focus(), 0);
                                    }}
                                  >
                                    ✏
                                  </button>
                                </>
                              )}
                            </div>
                            <span className="commodity-staged-item-file">{staged.fileName}</span>
                            <span className="commodity-staged-item-progress">
                              {staged.enrichedRows} / {staged.totalRows} {t('commodities.tradeBooking.rowsEnriched')}
                            </span>
                            {staged.updatedAt && (
                              <span className="commodity-staged-item-date">
                                {new Date(staged.updatedAt).toLocaleDateString(i18n.language)}
                              </span>
                            )}
                          </div>
                          <div className="commodity-staged-item-actions">
                            <button
                              className="import-btn import-btn-primary"
                              onClick={() => staged.id && handleResumeStagedImport(staged.id)}
                            >
                              {t('commodities.tradeBooking.resume')}
                            </button>
                            <button
                              className="import-btn import-btn-secondary"
                              onClick={() => {
                                const id = staged.id;
                                if (!id) return;
                                setConfirmModal({
                                  isOpen: true,
                                  title: t('commodities.tradeBooking.delete'),
                                  message: t('commodities.tradeBooking.confirmDeleteStaged'),
                                  onConfirm: () => {
                                    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
                                    handleDeleteStagedImport(id);
                                  },
                                });
                              }}
                            >
                              {t('commodities.tradeBooking.delete')}
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="commodity-import-empty">
                    <span>{t('commodities.tradeBooking.importEmpty')}</span>
                    {canImport && (
                      <button className="commodity-import-trigger-btn" onClick={handleStartImport}>
                        {t('commodities.tradeBooking.importButton')}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Step: Selecting config + file */}
            {importStep === 'selecting' && (
              <>
                {configs.length === 0 ? (
                  <div className="commodity-import-bar">
                    <span>{t('commodities.tradeBooking.noConfigs')}</span>
                    <button className="import-btn import-btn-secondary" onClick={handleCancelImport}>
                      {t('commodities.tradeBooking.cancel')}
                    </button>
                  </div>
                ) : (
                  <div className="commodity-import-form">
                    {/* Config selector */}
                    <div className="commodity-import-field">
                      <label className="commodity-import-label">
                        {t('commodities.tradeBooking.configLabel')}
                      </label>
                      <select
                        value={selectedConfigId}
                        onChange={(e) => {
                          setSelectedConfigId(e.target.value);
                          setFileBase64('');
                          setFileName('');
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        <option value="">{t('commodities.tradeBooking.selectConfig')}</option>
                        {configs.map((cfg) => (
                          <option key={cfg.id} value={cfg.id || ''}>
                            {cfg.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* File drop zone — visible once config is selected */}
                    {selectedConfigId && (
                      <div className="commodity-import-field">
                        <label className="commodity-import-label">
                          {t('commodities.tradeBooking.fileLabel')}
                        </label>
                        <div
                          className="commodity-file-dropzone"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept={fileAccept}
                            onChange={handleFileChange}
                          />
                          {fileName ? (
                            <span className="commodity-file-name">{fileName}</span>
                          ) : (
                            <span>{t('commodities.tradeBooking.dropzoneText')}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="commodity-import-actions">
                      <button
                        className="import-btn import-btn-primary"
                        disabled={!selectedConfigId || !fileBase64}
                        onClick={handlePreview}
                      >
                        {t('commodities.tradeBooking.preview')}
                      </button>
                      <button className="import-btn import-btn-secondary" onClick={handleCancelImport}>
                        {t('commodities.tradeBooking.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Step: Preview results */}
            {importStep === 'previewing' && previewResult && (
              <div className="commodity-preview-section">
                {/* Preview header */}
                <div className="commodity-preview-header">
                  <div>
                    <span className="commodity-preview-title">{t('commodities.tradeBooking.previewTitle')}</span>
                    <span className="commodity-preview-subtitle">{t('commodities.tradeBooking.previewSubtitle')}</span>
                  </div>
                  <div className="commodity-preview-actions">
                    {/* Stage for Enrichment — shown when there are deferred field warnings */}
                    {hasDeferredWarnings && (
                      <button
                        className="import-btn import-btn-primary"
                        disabled={previewResult.errorRows > 0 || importLoading}
                        onClick={handleStageImport}
                        title={previewResult.errorRows > 0 ? t('commodities.tradeBooking.errorsPreventStaging') : ''}
                      >
                        {t('commodities.tradeBooking.stageForEnrichment')} ({previewResult.validRows})
                      </button>
                    )}
                    {/* Direct Commit — shown when no deferred warnings */}
                    {!hasDeferredWarnings && (
                      <button
                        className="import-btn import-btn-primary"
                        disabled={previewResult.errorRows > 0 || importLoading}
                        onClick={handleCommit}
                        title={previewResult.errorRows > 0 ? t('commodities.tradeBooking.errorsPreventCommit') : ''}
                      >
                        {t('commodities.tradeBooking.commit')} ({previewResult.validRows})
                      </button>
                    )}
                    <button className="import-btn import-btn-secondary" onClick={handleCancelImport}>
                      {t('commodities.tradeBooking.cancel')}
                    </button>
                  </div>
                </div>

                {/* Summary bar — clickable filters */}
                <div className="commodity-preview-summary">
                  <button
                    className={`summary-filter ${previewFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setPreviewFilter('all')}
                  >
                    {t('commodities.tradeBooking.totalRows')}: <strong>{previewResult.totalRows}</strong>
                  </button>
                  <button
                    className={`summary-filter stat-valid ${previewFilter === 'valid' ? 'active' : ''}`}
                    onClick={() => setPreviewFilter('valid')}
                  >
                    {t('commodities.tradeBooking.validLabel')}: {previewResult.validRows}
                  </button>
                  {previewResult.errorRows > 0 && (
                    <button
                      className={`summary-filter stat-errors ${previewFilter === 'errors' ? 'active' : ''}`}
                      onClick={() => setPreviewFilter('errors')}
                    >
                      {t('commodities.tradeBooking.errorsLabel')}: {previewResult.errorRows}
                    </button>
                  )}
                  {previewResult.warningRows > 0 && (
                    <button
                      className={`summary-filter stat-warnings ${previewFilter === 'warnings' ? 'active' : ''}`}
                      onClick={() => setPreviewFilter('warnings')}
                    >
                      {t('commodities.tradeBooking.warningsLabel')}: {previewResult.warningRows}
                    </button>
                  )}
                  {previewResult.skippedRows > 0 && (
                    <span className="summary-stat stat-skipped">
                      {t('commodities.tradeBooking.skippedLabel')}: {previewResult.skippedRows}
                    </span>
                  )}
                </div>

                {/* Preview table */}
                <div className="commodity-preview-table-wrapper">
                  <table className="commodity-preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('commodities.tradeBooking.status')}</th>
                        {previewColumns.map((col) => (
                          <th key={col}>{getColumnLabel(col)}</th>
                        ))}
                        <th>{t('commodities.tradeBooking.issues')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPreviewTrades.map((row) => {
                        const hasErrors = row.errors.length > 0;
                        const hasWarnings = row.warnings.length > 0;
                        const rowClass = hasErrors ? 'row-error' : hasWarnings ? 'row-warning' : 'row-valid';
                        return (
                          <tr key={row.rowIndex} className={rowClass}>
                            <td>{row.rowIndex}</td>
                            <td className="row-status-cell">
                              {hasErrors ? (
                                <span className="status-error">&#x2718;</span>
                              ) : hasWarnings ? (
                                <span className="status-warning">&#x26A0;</span>
                              ) : (
                                <span className="status-ok">&#x2714;</span>
                              )}
                            </td>
                            {previewColumns.map((col) => (
                              <td key={col}>{formatCellValue(col, row.fields[col])}</td>
                            ))}
                            <td>
                              {hasErrors && (
                                <div className="error-details">
                                  {row.errors.map((e, i) => <div key={i}>{translateImportError(e, t)}</div>)}
                                </div>
                              )}
                              {hasWarnings && (
                                <div className="warning-details">
                                  {row.warnings.map((w, i) => <div key={i}>{translateImportError(w, t)}</div>)}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Step: Enriching — AG Grid editing surface */}
            {importStep === 'enriching' && currentStagedImport && (
              <div className="commodity-enrichment-section">
                <div className="commodity-enrichment-header">
                  <div className="commodity-enrichment-header-left">
                    <span className="commodity-preview-title">
                      {t('commodities.tradeBooking.enrichingTitle')}
                    </span>
                    <span className="commodity-preview-subtitle">
                      {currentStagedImport.configName} — {currentStagedImport.fileName}
                    </span>
                  </div>
                  <div className="commodity-enrichment-header-right">
                    <span className={`commodity-enrichment-progress ${enrichedCount === rowsNeedingEnrichment.size ? 'complete' : ''}`}>
                      {enrichedCount} / {rowsNeedingEnrichment.size} {t('commodities.tradeBooking.rowsEnriched')}
                    </span>
                    {enrichmentDirty && (
                      <span className="commodity-enrichment-dirty-badge">
                        {t('commodities.tradeBooking.unsavedLabel')}
                      </span>
                    )}
                    <button
                      className="import-btn import-btn-secondary"
                      disabled={savingDraft}
                      onClick={handleSaveDraft}
                    >
                      {savingDraft ? t('commodities.tradeBooking.saving') : t('commodities.tradeBooking.saveDraft')}
                    </button>
                    <button
                      className="import-btn import-btn-primary"
                      disabled={committingStaged}
                      onClick={handleCommitStaged}
                    >
                      {t('commodities.tradeBooking.commit')} ({currentStagedImport.totalRows})
                    </button>
                    <button
                      className="import-btn import-btn-secondary"
                      onClick={handleCloseEnrichment}
                    >
                      {t('commodities.tradeBooking.close')}
                    </button>
                  </div>
                </div>
                <div className="commodity-enrichment-grid ag-theme-alpine-dark" onPasteCapture={handleEnrichmentPaste}>
                  <AgGridReact
                    ref={enrichmentGridRef}
                    rowData={enrichmentRowData}
                    columnDefs={enrichmentColDefs}
                    defaultColDef={{
                      sortable: true,
                      filter: true,
                      resizable: true,
                    }}
                    localeText={localeText}
                    animateRows={false}
                    singleClickEdit={true}
                    stopEditingWhenCellsLoseFocus={true}
                    onCellValueChanged={handleEnrichmentCellChange}
                    getRowId={(params) => String(params.data.rowIndex)}
                    suppressCellFocus={false}
                  />
                </div>
              </div>
            )}

          </div>
        </Panel>

        <ResizeHandle direction="vertical" />

        {/* ── Bottom Panel: Trades Grid ── */}
        <Panel defaultSize={50} minSize={15}>
          <div className="commodity-booking-grid-panel">
            <div className="commodity-panel-header">
              <div className="commodity-panel-title">{t('commodities.tradeBooking.existingTrades')}</div>
            </div>
            <CommodityTradesGrid trades={trades} loading={tradesLoading} />
          </div>
        </Panel>
      </PanelGroup>

      {/* Full-screen loading overlay — uploading / staging / committing */}
      {(importStep === 'uploading' || importStep === 'staging' || importStep === 'committing') && (
        <div className="commodity-import-overlay">
          <div className="commodity-import-overlay-content">
            <div className="commodity-overlay-spinner" />
            <p>
              {importStep === 'uploading'
                ? t('commodities.tradeBooking.processingFile', { fileName })
                : importStep === 'staging'
                  ? t('commodities.tradeBooking.staging')
                  : t('commodities.tradeBooking.committing')}
            </p>
            <p className="commodity-overlay-subtitle">
              {importStep === 'uploading' && fileRowCount
                ? t('commodities.tradeBooking.processingRowCount', { count: fileRowCount })
                : t('commodities.tradeBooking.processingPatience')}
            </p>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast((prev) => ({ ...prev, visible: false }))}
      />

      {/* Confirm modal (delete staged import, etc.) */}
      <AlertModal
        isOpen={confirmModal.isOpen}
        type="warning"
        title={confirmModal.title}
        message={confirmModal.message}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        confirmLabel={confirmModal.confirmLabel || t('commodities.tradeBooking.delete')}
      />

      {/* Label modal — name the staged import before staging */}
      {labelModal.isOpen && (
        <div className="commodity-label-modal-backdrop" onClick={() => setLabelModal({ isOpen: false })}>
          <div className="commodity-label-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="commodity-label-modal-title">
              {t('commodities.tradeBooking.labelModal.title')}
            </h3>
            <div className="commodity-label-modal-body">
              <label className="commodity-label-modal-label">
                {t('commodities.tradeBooking.labelModal.label')}
              </label>
              <input
                className="commodity-label-modal-input"
                value={pendingLabel}
                onChange={(e) => setPendingLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleLabelConfirm(); }
                  if (e.key === 'Escape') setLabelModal({ isOpen: false });
                }}
                placeholder={t('commodities.tradeBooking.labelModal.placeholder')}
                autoFocus
              />
            </div>
            <div className="commodity-label-modal-actions">
              <button
                className="import-btn import-btn-secondary"
                onClick={() => setLabelModal({ isOpen: false })}
              >
                {t('commodities.tradeBooking.labelModal.cancel')}
              </button>
              <button
                className="import-btn import-btn-primary"
                onClick={handleLabelConfirm}
              >
                {t('commodities.tradeBooking.labelModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommodityTradeBooking;
